import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks for fetching and managing pledges
export const fetchPledges = createAsyncThunk(
  'donations/fetchPledges',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('donations');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const fetchPledgeById = createAsyncThunk(
  'donations/fetchPledgeById',
  async (pledgeId, { rejectWithValue }) => {
    try {
      const response = await api.get(`donations/${pledgeId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const createPledge = createAsyncThunk(
  'donations/createPledge',
  async (pledgeData, { rejectWithValue }) => {
    try {
      const response = await api.post('donations', pledgeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const updatePledge = createAsyncThunk(
  'donations/updatePledge',
  async ({ pledgeId, pledgeData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`donations/${pledgeId}`, pledgeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

export const deletePledge = createAsyncThunk(
  'donations/deletePledge',
  async (pledgeId, { rejectWithValue }) => {
    try {
      await api.delete(`donations/${pledgeId}`);
      return pledgeId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  },
);

const donationSlice = createSlice({
  name: 'donations',
  initialState: {
    pledges: [],
    currentPledge: null,
    loading: false,
    error: null,
    deleteError: null,
  },
  reducers: {
    // Clear error messages
    clearError: (state) => {
      state.error = null;
      state.deleteError = null;
    },

    // Clear current pledge
    clearCurrentPledge: (state) => {
      state.currentPledge = null;
    },

    // Update pledge in local state (for optimistic updates)
    updatePledgeLocal: (state, action) => {
      const index = state.pledges.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.pledges[index] = { ...state.pledges[index], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch all pledges
    builder
      .addCase(fetchPledges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPledges.fulfilled, (state, action) => {
        state.loading = false;
        state.pledges = action.payload;
        state.error = null;
      })
      .addCase(fetchPledges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch pledge by ID
    builder
      .addCase(fetchPledgeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPledgeById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPledge = action.payload;
        state.error = null;
      })
      .addCase(fetchPledgeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create pledge
    builder
      .addCase(createPledge.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPledge.fulfilled, (state, action) => {
        state.loading = false;
        state.pledges.push(action.payload);
        state.error = null;
      })
      .addCase(createPledge.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update pledge
    builder
      .addCase(updatePledge.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePledge.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.pledges.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.pledges[index] = action.payload;
        }
        if (state.currentPledge?.id === action.payload.id) {
          state.currentPledge = action.payload;
        }
        state.error = null;
      })
      .addCase(updatePledge.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete pledge
    builder
      .addCase(deletePledge.pending, (state) => {
        state.loading = true;
        state.deleteError = null;
      })
      .addCase(deletePledge.fulfilled, (state, action) => {
        state.loading = false;
        state.pledges = state.pledges.filter((p) => p.id !== action.payload);
        if (state.currentPledge?.id === action.payload) {
          state.currentPledge = null;
        }
        state.deleteError = null;
      })
      .addCase(deletePledge.rejected, (state, action) => {
        state.loading = false;
        state.deleteError = action.payload;
      });
  },
});

export const { clearError, clearCurrentPledge, updatePledgeLocal } = donationSlice.actions;

// Selectors
export const selectAllPledges = (state) => state.donations.pledges;
export const selectCurrentPledge = (state) => state.donations.currentPledge;
export const selectPledgesLoading = (state) => state.donations.loading;
export const selectPledgesError = (state) => state.donations.error;
export const selectDeleteError = (state) => state.donations.deleteError;

// Selector to get pledge by ID from the list
export const selectPledgeById = (state, pledgeId) => {
  return state.donations.pledges.find((p) => p.id === pledgeId);
};

// Selector to filter pledges by status
export const selectPledgesByStatus = (state, status) => {
  return state.donations.pledges.filter((p) => p.pledge_status === status);
};

// Selector to filter pledges by contact
export const selectPledgesByContact = (state, contactId) => {
  return state.donations.pledges.filter((p) => p.contact_id === contactId);
};

export default donationSlice.reducer;
